import type { Dates, Results } from "../../lib/ccApi";

/**
 * Command Center V2 (notes/COMMAND-CENTER-V2.md): the pace line every goal shows at every level, and the dates
 * in words. A goal is on pace when its recent rate, kept up, reaches the target by the goal's day.
 */

const DAY = 864e5;
const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const parseDay = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** "7 Oct". */
export const dayMon = (d: Date) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
/** "20 December". */
export const dayMonth = (d: Date) => `${d.getDate()} ${MONTHS_LONG[d.getMonth()]}`;

export interface PaceLine {
  kind: "on" | "behind" | "done";
  got: number;
  target: number;
  /** Where it should be by today, kept even from launch to the goal's day. */
  expected: number;
  /** Where it lands by the goal's day at the last two weeks' rate. */
  forecast: number;
  start: Date;
  end: Date;
  /** Days gone and days in all, launch to the goal's day. */
  gone: number;
  all: number;
}

/** The pace line from the goal's stored results; null before it has launched. */
export function paceLine(results: { totals: Pick<Results["totals"], "results">; days: { day: string; results: number }[] }, goal: { target: number; by: string }, launchedAt: string | null, done = false, now = new Date()): PaceLine | null {
  if (!launchedAt) return null;
  const start = startOf(new Date(launchedAt));
  const end = parseDay(goal.by);
  const today = startOf(now);
  const all = Math.max(1, Math.round((end.getTime() - start.getTime()) / DAY) + 1);
  const gone = Math.min(all, Math.max(1, Math.round((today.getTime() - start.getTime()) / DAY) + 1));
  const got = results.totals.results;
  if (done) return { kind: "done", got, target: goal.target, expected: goal.target, forecast: got, start, end, gone: all, all };
  const recent = results.days.toSorted((a, b) => a.day.localeCompare(b.day)).slice(-14);
  const perDay = recent.length ? recent.reduce((t, d) => t + d.results, 0) / recent.length : gone ? got / gone : 0;
  const forecast = got + perDay * Math.max(0, all - gone);
  return { kind: forecast >= goal.target ? "on" : "behind", got, target: goal.target, expected: (goal.target * gone) / all, forecast, start, end, gone, all };
}

/** The days a choice of dates covers, as Meta counts them (its "last 7 days" ends yesterday); null for since launch with no launch. */
export function datesOf(d: Dates, launchedAt: string | null, now = new Date()): [Date, Date] | null {
  if ("from" in d) return [parseDay(d.from), parseDay(d.to)];
  const t = startOf(now);
  const back = (n: number) => new Date(t.getTime() - n * DAY);
  switch (d.preset) {
    case "today":
      return [t, t];
    case "yesterday":
      return [back(1), back(1)];
    case "last_7d":
      return [back(7), back(1)];
    case "last_14d":
      return [back(14), back(1)];
    case "last_30d":
      return [back(30), back(1)];
    case "this_month":
      return [new Date(t.getFullYear(), t.getMonth(), 1), t];
    case "last_month":
      return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)];
    default:
      return launchedAt ? [startOf(new Date(launchedAt)), t] : null;
  }
}

/** A day as YYYY-MM-DD in local time, the way the stored daily results name their days. */
export const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** "30 Sep to 6 Oct", or one day. */
export const daysWords = (r: [Date, Date] | null, none = "From each launch") => (!r ? none : r[0].getTime() === r[1].getTime() ? dayMon(r[0]) : `${dayMon(r[0])} to ${dayMon(r[1])}`);

/** The dates inside a sentence: "in the last 7 days", "today", "since launch", "from 1 Oct to 6 Oct". */
export function datesInWords(d: Dates, label: string): string {
  if ("from" in d) return `from ${daysWords(datesOf(d, null))}`;
  if (d.preset === "today" || d.preset === "yesterday") return d.preset;
  if (d.preset === "maximum") return "since launch";
  return `in the ${label.toLowerCase()}`.replace("in the this month", "this month").replace("in the last month", "last month");
}
